import { isPraise } from '@/lib/who-match';
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { Prayer } from '@/types/prayer';
import {
  canonicalLocale,
  formatShortDateTime,
  formatSpanBetween,
  isSelf,
} from '@/lib/prayer-utils';
import { isGeneral, whoOfWithMap, type WhoMergeMap } from '@/lib/who-match';
import {
  buildRememberSearchIndex,
  deriveMoments,
  findAnniversaryMoment,
  groupMomentsByPerson,
  interleaveYearMarkers,
  momentBody,
  rememberSearchIndexSteps,
  searchRememberIndex,
  type Moment,
  type MomentKind,
  type RememberSearchIndex,
  type RememberSearchMatchKind,
  type RememberSearchResult,
  type MomentThread,
  type StreamItem,
} from '@/lib/remember-moments';
import { runStepsInSlices } from '@/lib/search-prayers';
import {
  buildRememberVirtualLayout,
  rememberAnchorScrollTop,
  REMEMBER_SCROLL_SETTLE_MS,
  rememberLayoutRetainedBytes,
  rememberPendingNearEdge,
  rememberReaderAnchor,
  rememberStickyYear,
  rememberVisibleRange,
  type RememberReaderAnchor,
} from '@/lib/remember-virtualization';
import { expandQueryToSynonymTerms } from '@/lib/topic-search';
import {
  boundDiscoverySearchInput,
  DISCOVERY_SEARCH_MAX_QUERY_LENGTH,
} from '@/lib/discovery-search';
import { useSettingsStore } from '@/lib/settings-store';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useAuth } from '@/hooks/useAuth';
import { useDialogFocus } from '@/hooks/useDialogFocus';
import { useNoticeStackReserve } from '@/hooks/useNoticeStackReserve';
import { useNavLayer, useNavStack } from '@/hooks/useNavLayer';
import { useScrollFreezeRecovery } from '@/hooks/useScrollFreezeRecovery';
import { getAuthEpoch } from '@/lib/keepsake/epochs';
import styles from './RememberStream.module.css';

interface RememberStreamProps {
  prayers: Prayer[];
  onClose: () => void;
  onNew: () => void;
  onPrayerTap: (id: string) => void;
  onShareMoment: (prayer: Prayer) => void;
  zIndex?: number;
}

type TFn = (key: string, opts?: Record<string, unknown>) => string;
type Lens = 'time' | 'people';
type RememberTextScale = 'default' | 'large' | 'xl';
// Why Remember wrote scrollTop: a surface or recovery restore, an idle anchor
// correction, a settled held-back correction, a near-end flush, or a flush
// before a surface transition.
type ScrollWriteKind = 'restore' | 'anchor' | 'settle' | 'edge' | 'transition';

const PAGE_SIZE = 50;
const STREAM_OVERSCAN_PX = 720;

function localDayKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

function useLocalDay(): Date {
  const [day, setDay] = useState(() => localDayKey(new Date()));
  useEffect(() => {
    let timer = 0;
    const refresh = () => setDay(localDayKey(new Date()));
    const schedule = () => {
      window.clearTimeout(timer);
      const now = new Date();
      const next = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate() + 1,
        0,
        0,
        1,
      );
      timer = window.setTimeout(() => {
        refresh();
        schedule();
      }, Math.max(1000, next.getTime() - now.getTime()));
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        refresh();
        schedule();
      }
    };
    schedule();
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('focus', onVisible);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);
  const [year, month, date] = day.split('-').map(Number);
  return useMemo(
    () => new Date(year, month, date, 12),
    [date, month, year],
  );
}

function estimateItemHeight(
  item: StreamItem,
  scale: RememberTextScale,
): number {
  const factor = scale === 'xl' ? 1.25 : scale === 'large' ? 1.13 : 1;
  if (item.kind === 'year') return Math.round(88 * factor);
  if (item.moment.kind === 'testimony') return Math.round(310 * factor);
  if (item.moment.kind === 'answered') return Math.round(270 * factor);
  return Math.round(235 * factor);
}

function wrapperScrollOffset(scroll: HTMLElement, wrapper: HTMLElement): number {
  return (
    wrapper.getBoundingClientRect().top -
    scroll.getBoundingClientRect().top +
    scroll.scrollTop
  );
}

function pageCount(total: number): number {
  return Math.max(1, Math.ceil(total / PAGE_SIZE));
}

function forwardPage<T>(values: readonly T[], page: number): T[] {
  const start = page * PAGE_SIZE;
  return values.slice(start, start + PAGE_SIZE);
}

function reverseChronologyPage<T>(values: readonly T[], page: number): T[] {
  const end = Math.max(0, values.length - page * PAGE_SIZE);
  const start = Math.max(0, end - PAGE_SIZE);
  return values.slice(start, end);
}

function sparkClass(kind: MomentKind): string {
  if (kind === 'testimony') return styles.sparkTestimony;
  if (kind === 'answered') return styles.sparkAnswered;
  return styles.sparkProgress;
}

interface MeasuredSlotProps {
  item: StreamItem;
  top: number;
  estimate: number;
  onMeasure: (key: string, height: number) => void;
  children: ReactNode;
}

function MeasuredSlot({
  item,
  top,
  estimate,
  onMeasure,
  children,
}: MeasuredSlotProps) {
  const slotRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const node = slotRef.current;
    if (!node) return;
    const measure = () => onMeasure(item.key, Math.ceil(node.getBoundingClientRect().height));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [item.key, onMeasure]);

  let slotClass = styles.slotProgress;
  if (item.kind === 'year') slotClass = styles.slotYear;
  else if (item.moment.kind === 'testimony') slotClass = styles.slotTestimony;
  else if (item.moment.kind === 'answered') slotClass = styles.slotAnswered;
  return (
    <div
      ref={slotRef}
      className={`${styles.streamSlot} ${slotClass}`}
      style={{ transform: `translateY(${top}px)`, minHeight: estimate }}
      data-virtual-key={item.key}
      data-moment-id={item.kind === 'moment' ? item.moment.id : undefined}
    >
      {children}
    </div>
  );
}

export default function RememberStream(props: RememberStreamProps) {
  const { session } = useAuth();
  const searchScopeKey = `${session?.user.id ?? 'signed-out'}:${getAuthEpoch()}`;
  return (
    <ScopedRememberStream
      key={searchScopeKey}
      {...props}
      searchScopeKey={searchScopeKey}
    />
  );
}

function ScopedRememberStream({
  prayers,
  onClose,
  onNew,
  onPrayerTap,
  onShareMoment,
  zIndex,
  searchScopeKey,
}: RememberStreamProps & { searchScopeKey: string }) {
  const { t, i18n } = useTranslation();
  const locale = canonicalLocale(i18n.language);
  const textScale = useSettingsStore((state) => state.textScale);
  const mergeMap = useSettingsStore((state) => state.whoMergeMap);
  const today = useLocalDay();
  const selfLabel = t('entry.praiseLabel');
  const generalLabel = t('people.general');

  const moments = useMemo(() => deriveMoments(prayers.filter(isPraise)), [prayers]);
  const items = useMemo(() => interleaveYearMarkers(moments), [moments]);
  const threads = useMemo(
    () => groupMomentsByPerson(moments, mergeMap),
    [moments, mergeMap],
  );
  // P2 (garden-remember readiness, 2026-10-16): no search index is built
  // during render. After paint the whole index is built in bounded idle
  // slices and published here; a change of moments, scope, merge map or labels
  // cancels the obsolete build. Until the index for exactly these inputs is
  // ready, an empty index answers and the existing searching state shows.
  const [builtSearch, setBuiltSearch] = useState<{
    moments: readonly Moment[];
    scopeKey: string;
    mergeMap: typeof mergeMap;
    self: string;
    general: string;
    index: RememberSearchIndex;
  } | null>(null);
  useEffect(
    () => runStepsInSlices(
      rememberSearchIndexSteps(moments, {
        scopeKey: searchScopeKey,
        mergeMap,
        labels: { self: selfLabel, general: generalLabel, praise: selfLabel },
      }),
      (index) => setBuiltSearch({
        moments,
        scopeKey: searchScopeKey,
        mergeMap,
        self: selfLabel,
        general: generalLabel,
        index,
      }),
    ),
    [generalLabel, mergeMap, moments, searchScopeKey, selfLabel],
  );
  const emptySearchIndex = useMemo(
    () => buildRememberSearchIndex([], { scopeKey: searchScopeKey }),
    [searchScopeKey],
  );

  const [lens, setLens] = useState<Lens>('time');
  const [threadKey, setThreadKey] = useState<string | null>(null);
  const [peoplePage, setPeoplePage] = useState(0);
  const [threadPage, setThreadPage] = useState(0);
  const [searchPaging, setSearchPaging] = useState<{
    revision: symbol | null;
    locale: string;
    query: string;
    page: number;
  }>(() => ({ revision: null, locale: '', query: '', page: 0 }));
  const [searchActive, setSearchActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedQuery = useDebouncedValue(searchQuery, 150);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchTriggerRef = useRef<HTMLButtonElement>(null);
  const searchOpenerRef = useRef<HTMLElement | null>(null);
  const threadTriggerRef = useRef<HTMLElement | null>(null);
  const restoreSearchAnchorRef = useRef(false);
  // App notice stack: the in-flow spacer below the top bar keeps `.scroll`
  // (hero, lens row, search, stream) below the fixed notices. Chrome only;
  // the stream's own measurement and scroll writes are untouched.
  const [noticeReserve, noticeReserveRef] = useNoticeStackReserve();

  const activeThread = useMemo(
    () =>
      threadKey === null
        ? null
        : threads.find((thread) => thread.key === threadKey) ?? null,
    [threadKey, threads],
  );

  const trimmedQuery = debouncedQuery.trim();
  const hasRememberQuery = trimmedQuery !== '';
  const realSearchIndex =
    builtSearch &&
    builtSearch.moments === moments &&
    builtSearch.scopeKey === searchScopeKey &&
    builtSearch.mergeMap === mergeMap &&
    builtSearch.self === selfLabel &&
    builtSearch.general === generalLabel
      ? builtSearch.index
      : null;
  const searchIndex = realSearchIndex ?? emptySearchIndex;
  const searchPending =
    (searchQuery.trim() !== '' && debouncedQuery !== searchQuery) ||
    (hasRememberQuery && realSearchIndex === null);
  const searchPage =
    searchPaging.revision === searchIndex.revision &&
    searchPaging.locale === locale &&
    searchPaging.query === trimmedQuery
      ? searchPaging.page
      : 0;
  const searchResult = useMemo(() => {
    const terms = locale === 'en-US' ? expandQueryToSynonymTerms(trimmedQuery) : [];
    return searchRememberIndex(
      searchIndex,
      trimmedQuery,
      terms,
      PAGE_SIZE,
      undefined,
      searchPage * PAGE_SIZE,
    );
  }, [locale, searchIndex, searchPage, trimmedQuery]);
  const changeSearchPage = useCallback(
    (page: number) => {
      setSearchPaging({ revision: searchIndex.revision, locale, query: trimmedQuery, page });
    },
    [locale, searchIndex, trimmedQuery],
  );

  const anniversary = useMemo(() => {
    const moment = findAnniversaryMoment(moments, today);
    if (!moment) return null;
    return {
      moment,
      yearsAgo: today.getFullYear() - new Date(moment.date).getFullYear(),
    };
  }, [moments, today]);

  const restoreChildOpener = useCallback((opener: HTMLElement | null) => {
    requestAnimationFrame(() => {
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    });
  }, []);
  const closeThread = useCallback(() => {
    setThreadKey(null);
    restoreChildOpener(threadTriggerRef.current);
  }, [restoreChildOpener]);
  const closeSearch = useCallback(() => {
    setSearchActive(false);
    requestAnimationFrame(() => {
      const trigger = searchTriggerRef.current;
      const opener = searchOpenerRef.current;
      if (trigger?.isConnected) trigger.focus({ preventScroll: true });
      else if (opener?.isConnected) opener.focus({ preventScroll: true });
    });
  }, []);

  const nav = useNavStack();
  useNavLayer('remember-thread', threadKey !== null, closeThread);
  useNavLayer('remember-search', searchActive, closeSearch);

  const threadIsTop = useSyncExternalStore(nav.subscribe, () =>
    nav.isTopLayer('remember-thread'),
  );
  const searchIsTop = useSyncExternalStore(nav.subscribe, () =>
    nav.isTopLayer('remember-search'),
  );
  const rememberOwnsFocus = useSyncExternalStore(nav.subscribe, () =>
    nav.isTopLayer('remember') ||
    nav.isTopLayer('remember-thread') ||
    nav.isTopLayer('remember-search'),
  );

  useLayoutEffect(() => {
    if (
      threadKey !== null &&
      threadIsTop &&
      !threads.some((thread) => thread.key === threadKey) &&
      nav.has('remember-thread')
    ) {
      nav.requestClose('remember-thread');
    }
  }, [nav, threadIsTop, threadKey, threads]);

  useLayoutEffect(() => {
    if (searchActive && searchIsTop && moments.length === 0 && nav.has('remember-search')) {
      nav.requestClose('remember-search');
    }
  }, [moments.length, nav, searchActive, searchIsTop]);

  const requestBack = useCallback(() => {
    if (searchActive && nav.has('remember-search')) {
      nav.requestClose('remember-search');
      return;
    }
    if (threadKey !== null && nav.has('remember-thread')) {
      nav.requestClose('remember-thread');
      return;
    }
    if (nav.has('remember')) nav.requestClose('remember');
    else onClose();
  }, [nav, onClose, searchActive, threadKey]);
  const requestEscape = useCallback(() => {
    if (searchActive && searchQuery !== '') {
      setSearchPaging({ revision: null, locale: '', query: '', page: 0 });
      setSearchQuery('');
      requestAnimationFrame(() => searchInputRef.current?.focus({ preventScroll: true }));
      return;
    }
    requestBack();
  }, [requestBack, searchActive, searchQuery]);

  const rememberRef = useRef<HTMLDivElement>(null);
  const backRef = useRef<HTMLButtonElement>(null);
  useDialogFocus({
    open: true,
    active: rememberOwnsFocus,
    containerRef: rememberRef,
    initialFocusRef: backRef,
    onEscape: requestEscape,
  });

  useEffect(() => {
    if (searchActive) searchInputRef.current?.focus({ preventScroll: true });
  }, [searchActive]);

  const surfaceKey = searchActive
    ? 'search'
    : threadKey !== null
      ? `thread:${threadKey}`
      : lens;
  const surfaceKeyRef = useRef(surfaceKey);
  const scrollPositionsRef = useRef(new Map<string, number>());
  const scrollRef = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const readerAnchorRef = useRef<RememberReaderAnchor | null>(null);
  const focusedMomentIdRef = useRef<string | null>(null);
  useLayoutEffect(() => {
    surfaceKeyRef.current = surfaceKey;
  }, [surfaceKey]);

  const [measuredState, setMeasuredState] = useState<{
    scale: RememberTextScale;
    heights: ReadonlyMap<string, number>;
  }>(() => ({ scale: textScale, heights: new Map() }));
  const measuredHeights = useMemo<ReadonlyMap<string, number>>(
    () =>
      measuredState.scale === textScale
        ? measuredState.heights
        : new Map<string, number>(),
    [measuredState, textScale],
  );
  const layout = useMemo(
    () =>
      buildRememberVirtualLayout(
        items,
        measuredHeights,
        (item) => estimateItemHeight(item, textScale),
      ),
    [items, measuredHeights, textScale],
  );
  const layoutRef = useRef(layout);
  const previousLayoutRef = useRef(layout);
  useLayoutEffect(() => {
    layoutRef.current = layout;
  }, [layout]);

  const captureReaderAnchor = useCallback(() => {
    const scroll = scrollRef.current;
    const wrapper = wrapRef.current;
    if (!scroll || !wrapper) return;
    const offset = wrapperScrollOffset(scroll, wrapper);
    const focused =
      document.activeElement instanceof HTMLElement
        ? document.activeElement.closest<HTMLElement>('[data-moment-id]')?.dataset
            .momentId ?? focusedMomentIdRef.current
        : focusedMomentIdRef.current;
    readerAnchorRef.current = rememberReaderAnchor(
      layoutRef.current,
      scroll.scrollTop - offset,
      focused,
    );
    scrollPositionsRef.current.set(surfaceKeyRef.current, scroll.scrollTop);
  }, []);

  const scrollRecoverKey = useScrollFreezeRecovery(
    scrollRef,
    rememberRef,
    captureReaderAnchor,
  );
  const restoredRecoveryKeyRef = useRef(scrollRecoverKey);

  const pendingMeasurementsRef = useRef(
    new Map<string, { height: number; scale: RememberTextScale }>(),
  );
  const measurementFrameRef = useRef<number | null>(null);
  const onMeasure = useCallback((key: string, height: number) => {
    pendingMeasurementsRef.current.set(key, { height, scale: textScale });
    if (measurementFrameRef.current !== null) return;
    measurementFrameRef.current = requestAnimationFrame(() => {
      measurementFrameRef.current = null;
      const pending = Array.from(pendingMeasurementsRef.current);
      pendingMeasurementsRef.current.clear();
      const targetScale = pending.at(-1)?.[1].scale;
      if (!targetScale) return;
      setMeasuredState((current) => {
        const heights = new Map(
          current.scale === targetScale ? current.heights : undefined,
        );
        let changed = current.scale !== targetScale;
        for (const [pendingKey, measurement] of pending) {
          if (measurement.scale !== targetScale) continue;
          if (Math.abs((heights.get(pendingKey) ?? -1) - measurement.height) < 1) {
            continue;
          }
          heights.set(pendingKey, measurement.height);
          changed = true;
        }
        return changed ? { scale: targetScale, heights } : current;
      });
    });
  }, [textScale]);

  useEffect(
    () => () => {
      if (measurementFrameRef.current !== null) {
        cancelAnimationFrame(measurementFrameRef.current);
      }
    },
    [],
  );

  const [viewport, setViewport] = useState({
    localTop: 0,
    height: 0,
    wrapperOffset: 0,
  });
  const scrollFrameRef = useRef<number | null>(null);

  // Anchor corrections never write scrollTop while the reader is scrolling:
  // iOS stops a momentum glide on scrollTop writes. A held-back correction is
  // drawn by offsetting the (position: relative) stream wrapper with `top`,
  // which wrapperScrollOffset() already reads, so the visible range, sticky
  // year and reader anchor need no extra arithmetic. It is written once when
  // scrolling settles, on a surface transition, or near either end of the
  // stream. The value belongs to one wrapper element; a remount drops it.
  const pendingShiftRef = useRef<{ wrapper: HTMLElement | null; value: number }>({
    wrapper: null,
    value: 0,
  });
  const touchActiveRef = useRef(false);
  const lastScrollAtRef = useRef(Number.NEGATIVE_INFINITY);
  const settleTimerRef = useRef<number | null>(null);
  const syncViewportRef = useRef<() => void>(() => {});

  const writeScrollTop = useCallback(
    (scroll: HTMLElement, top: number, kind: ScrollWriteKind) => {
      // Diagnostic label for probes that observe scrollTop writes.
      if (rememberRef.current) rememberRef.current.dataset.scrollWriteKind = kind;
      scroll.scrollTop = top;
    },
    [],
  );

  const readPendingShift = useCallback((): number => {
    const pending = pendingShiftRef.current;
    if (pending.wrapper !== null && pending.wrapper !== wrapRef.current) {
      pendingShiftRef.current = { wrapper: null, value: 0 };
      return 0;
    }
    return pending.value;
  }, []);

  const clearSettleTimer = useCallback(() => {
    if (settleTimerRef.current !== null) {
      window.clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
  }, []);

  const flushPendingShift = useCallback(
    (kind: 'settle' | 'edge' | 'transition') => {
      clearSettleTimer();
      const pending = readPendingShift();
      pendingShiftRef.current = { wrapper: null, value: 0 };
      const scroll = scrollRef.current;
      const wrapper = wrapRef.current;
      if (wrapper) wrapper.style.top = '';
      if (!scroll || pending === 0) return;
      // Same task as clearing `top`: nothing visibly moves.
      writeScrollTop(scroll, scroll.scrollTop + pending, kind);
      scrollPositionsRef.current.set('time', scroll.scrollTop);
      syncViewportRef.current();
    },
    [clearSettleTimer, readPendingShift, writeScrollTop],
  );

  const scrollIsActive = useCallback(
    () =>
      touchActiveRef.current ||
      performance.now() - lastScrollAtRef.current < REMEMBER_SCROLL_SETTLE_MS,
    [],
  );

  const scheduleSettle = useCallback(() => {
    if (settleTimerRef.current !== null || readPendingShift() === 0) return;
    const check = () => {
      settleTimerRef.current = null;
      if (readPendingShift() === 0) return;
      const quietFor = performance.now() - lastScrollAtRef.current;
      if (touchActiveRef.current || quietFor < REMEMBER_SCROLL_SETTLE_MS) {
        settleTimerRef.current = window.setTimeout(
          check,
          Math.max(16, REMEMBER_SCROLL_SETTLE_MS - quietFor),
        );
        return;
      }
      flushPendingShift('settle');
    };
    settleTimerRef.current = window.setTimeout(check, REMEMBER_SCROLL_SETTLE_MS);
  }, [flushPendingShift, readPendingShift]);

  useEffect(() => clearSettleTimer, [clearSettleTimer]);

  useLayoutEffect(() => {
    const scroll = scrollRef.current;
    if (!scroll) return;
    const recovering = restoredRecoveryKeyRef.current !== scrollRecoverKey;
    restoredRecoveryKeyRef.current = scrollRecoverKey;
    if (recovering) {
      // beforeRecover already captured the reader anchor against the old,
      // offset wrapper; the remounted wrapper starts with no offset.
      clearSettleTimer();
      pendingShiftRef.current = { wrapper: null, value: 0 };
    }
    const wrapper = wrapRef.current;
    const anchor = readerAnchorRef.current;
    if (surfaceKey === 'time' && restoreSearchAnchorRef.current && wrapper && anchor) {
      restoreSearchAnchorRef.current = false;
      const offset = wrapperScrollOffset(scroll, wrapper);
      const anchoredTop = rememberAnchorScrollTop(layoutRef.current, anchor, offset);
      if (anchoredTop !== null) {
        writeScrollTop(scroll, anchoredTop, 'restore');
        scrollPositionsRef.current.set('time', anchoredTop);
        return;
      }
    }
    if (recovering && surfaceKey === 'time' && wrapper && anchor) {
      const offset = wrapperScrollOffset(scroll, wrapper);
      const anchoredTop = rememberAnchorScrollTop(layoutRef.current, anchor, offset);
      if (anchoredTop !== null) {
        writeScrollTop(scroll, anchoredTop, 'restore');
        scrollPositionsRef.current.set('time', anchoredTop);
        return;
      }
    }
    const saved = scrollPositionsRef.current.get(surfaceKey) ?? 0;
    writeScrollTop(scroll, saved, 'restore');
  }, [clearSettleTimer, scrollRecoverKey, surfaceKey, writeScrollTop]);

  // Every surface owns an independent position. One listener survives surface
  // switches and reads the current key ref, so the programmatic child reset
  // cannot race an old closure and overwrite the opener's saved position.
  // Explicit transition handlers below snapshot the opener synchronously.
  useLayoutEffect(() => {
    const scroll = scrollRef.current;
    if (!scroll) return;
    const save = () => {
      scrollPositionsRef.current.set(surfaceKeyRef.current, scroll.scrollTop);
    };
    scroll.addEventListener('scroll', save, { passive: true });
    return () => {
      save();
      scroll.removeEventListener('scroll', save);
    };
  }, [scrollRecoverKey]);

  useLayoutEffect(() => {
    const scroll = scrollRef.current;
    const wrapper = wrapRef.current;
    if (!scroll || !wrapper || lens !== 'time' || searchActive || threadKey !== null) {
      return;
    }
    const update = () => {
      scrollFrameRef.current = null;
      const offset = wrapperScrollOffset(scroll, wrapper);
      const localTop = scroll.scrollTop - offset;
      const pending = readPendingShift();
      if (
        pending !== 0 &&
        rememberPendingNearEdge(
          pending,
          localTop,
          scroll.clientHeight,
          layoutRef.current.total,
          scroll.clientHeight + STREAM_OVERSCAN_PX,
        )
      ) {
        // Re-enters update() with no pending offset.
        flushPendingShift('edge');
        return;
      }
      setViewport({ localTop, height: scroll.clientHeight, wrapperOffset: offset });
      scrollPositionsRef.current.set('time', scroll.scrollTop);
      readerAnchorRef.current = rememberReaderAnchor(layoutRef.current, localTop);
    };
    const onScroll = () => {
      lastScrollAtRef.current = performance.now();
      scrollPositionsRef.current.set('time', scroll.scrollTop);
      if (scrollFrameRef.current !== null) return;
      scrollFrameRef.current = requestAnimationFrame(update);
    };
    syncViewportRef.current = update;
    update();
    scroll.addEventListener('scroll', onScroll, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(scroll);
    observer.observe(wrapper);
    return () => {
      scroll.removeEventListener('scroll', onScroll);
      observer.disconnect();
      syncViewportRef.current = () => {};
      if (scrollFrameRef.current !== null) cancelAnimationFrame(scrollFrameRef.current);
      scrollFrameRef.current = null;
    };
  }, [
    flushPendingShift,
    items.length,
    lens,
    readPendingShift,
    scrollRecoverKey,
    searchActive,
    threadKey,
  ]);

  // Finger-down state follows the scroll element alone, so a rebind of the
  // viewport effect above (items.length, surface changes) never ends a held
  // gesture. Only a new scroll element (recovery remount) or unmount resets
  // it: a touch that began on the old element ends there.
  useLayoutEffect(() => {
    const scroll = scrollRef.current;
    if (!scroll) return;
    const onTouchStart = () => {
      touchActiveRef.current = true;
      lastScrollAtRef.current = performance.now();
    };
    const onTouchEnd = () => {
      touchActiveRef.current = false;
      lastScrollAtRef.current = performance.now();
      scheduleSettle();
    };
    scroll.addEventListener('touchstart', onTouchStart, { passive: true });
    scroll.addEventListener('touchend', onTouchEnd, { passive: true });
    scroll.addEventListener('touchcancel', onTouchEnd, { passive: true });
    return () => {
      scroll.removeEventListener('touchstart', onTouchStart);
      scroll.removeEventListener('touchend', onTouchEnd);
      scroll.removeEventListener('touchcancel', onTouchEnd);
      touchActiveRef.current = false;
    };
  }, [scheduleSettle, scrollRecoverKey]);

  useLayoutEffect(() => {
    const previousLayout = previousLayoutRef.current;
    if (previousLayout === layout) return;
    const scroll = scrollRef.current;
    const wrapper = wrapRef.current;
    if (!scroll || !wrapper || surfaceKey !== 'time') {
      previousLayoutRef.current = layout;
      return;
    }
    const offset = wrapperScrollOffset(scroll, wrapper);
    // Capture against the PREVIOUS monotonic geometry before swapping the
    // ref. This is the only reliable anchor during corpus insertion and
    // ResizeObserver reflow: readerAnchorRef may describe an earlier frame,
    // while the previous layout + current scrollTop describe exactly what the
    // reader was looking at immediately before this layout commit.
    const focused =
      document.activeElement instanceof HTMLElement
        ? document.activeElement.closest<HTMLElement>('[data-moment-id]')?.dataset
            .momentId ?? focusedMomentIdRef.current
        : focusedMomentIdRef.current;
    const anchor = rememberReaderAnchor(
      previousLayout,
      scroll.scrollTop - offset,
      focused,
    );
    previousLayoutRef.current = layout;
    if (!anchor) return;
    const nextTop = rememberAnchorScrollTop(layout, anchor, offset);
    if (nextTop === null) return;
    const delta = nextTop - scroll.scrollTop;
    // A correction under 1px changes nothing; never write it.
    if (Math.abs(delta) >= 1) {
      if (scrollIsActive()) {
        const pending = readPendingShift() + delta;
        if (Math.abs(pending) < 1) {
          pendingShiftRef.current = { wrapper: null, value: 0 };
          wrapper.style.top = '';
        } else {
          pendingShiftRef.current = { wrapper, value: pending };
          wrapper.style.top = `${-pending}px`;
        }
        // No scroll event follows a `top` change: resync now (this may flush
        // near an end of the stream).
        syncViewportRef.current();
        scheduleSettle();
      } else {
        writeScrollTop(scroll, nextTop, 'anchor');
      }
    }
    scrollPositionsRef.current.set('time', scroll.scrollTop);
    readerAnchorRef.current = anchor;
    if (anchor.focusedMomentId) {
      requestAnimationFrame(() => {
        rememberRef.current
          ?.querySelector<HTMLElement>(
            `[data-moment-id="${CSS.escape(anchor.focusedMomentId ?? '')}"] button`,
          )
          ?.focus({ preventScroll: true });
      });
    }
  }, [layout, readPendingShift, scheduleSettle, scrollIsActive, surfaceKey, writeScrollTop]);

  const range =
    viewport.height > 0
      ? rememberVisibleRange(
          layout,
          viewport.localTop,
          viewport.height,
          STREAM_OVERSCAN_PX,
        )
      : { start: 0, end: Math.min(8, items.length) };
  const visibleIndices: number[] = [];
  for (let index = range.start; index < range.end; index += 1) {
    visibleIndices.push(index);
  }
  const stickyYear = rememberStickyYear(layout, viewport.localTop);

  useLayoutEffect(() => {
    const focusedMomentId = focusedMomentIdRef.current;
    if (!focusedMomentId || surfaceKey !== 'time') return;
    const activeMoment =
      document.activeElement instanceof HTMLElement
        ? document.activeElement.closest<HTMLElement>('[data-moment-id]')?.dataset
            .momentId ?? null
        : null;
    if (activeMoment === focusedMomentId) return;
    const target = rememberRef.current?.querySelector<HTMLElement>(
      `[data-moment-id="${CSS.escape(focusedMomentId)}"] button`,
    );
    target?.focus({ preventScroll: true });
  }, [layout, range.end, range.start, scrollRecoverKey, surfaceKey]);

  const saveCurrentSurfacePosition = () => {
    const scroll = scrollRef.current;
    if (scroll) {
      scrollPositionsRef.current.set(surfaceKeyRef.current, scroll.scrollTop);
    }
  };

  const switchLens = (next: Lens) => {
    flushPendingShift('transition');
    saveCurrentSurfacePosition();
    setPeoplePage(0);
    setLens(next);
  };
  const openThread = (key: string) => {
    flushPendingShift('transition');
    saveCurrentSurfacePosition();
    threadTriggerRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    scrollPositionsRef.current.delete(`thread:${key}`);
    setThreadPage(0);
    setThreadKey(key);
  };
  const openSearch = () => {
    flushPendingShift('transition');
    saveCurrentSurfacePosition();
    restoreSearchAnchorRef.current = lens === 'time';
    if (lens === 'time') captureReaderAnchor();
    searchOpenerRef.current =
      document.activeElement instanceof HTMLButtonElement
        ? document.activeElement
        : searchTriggerRef.current;
    scrollPositionsRef.current.delete('search');
    setSearchPaging({ revision: null, locale: '', query: '', page: 0 });
    setSearchQuery('');
    setSearchActive(true);
  };
  const changeSearch = (query: string) => {
    setSearchPaging({ revision: null, locale: '', query: '', page: 0 });
    setSearchQuery(boundDiscoverySearchInput(query));
  };
  const openPrayer = (id: string) => {
    captureReaderAnchor();
    onPrayerTap(id);
  };

  const isEmpty = prayers.length === 0 || moments.length === 0;

  return (
    <div
      ref={rememberRef}
      className={styles.remember}
      style={{ zIndex }}
      role="dialog"
      aria-modal="true"
      aria-label={t('remember.title')}
      tabIndex={-1}
      data-remember-surface={surfaceKey}
      data-total-moments={moments.length}
      data-total-stream-items={items.length}
      data-mounted-stream-items={visibleIndices.length}
      data-visible-start={range.start}
      data-visible-end={range.end}
      data-wrapper-offset={Math.round(viewport.wrapperOffset)}
      data-layout-bytes={rememberLayoutRetainedBytes(layout)}
      onFocusCapture={(event) => {
        focusedMomentIdRef.current =
          event.target instanceof HTMLElement
            ? event.target.closest<HTMLElement>('[data-moment-id]')?.dataset.momentId ?? null
            : null;
      }}
    >
      <header className={styles.topBar}>
        <button
          ref={backRef}
          type="button"
          className={styles.back}
          onClick={requestBack}
          aria-label={t('nav.back')}
        >
          <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <polyline points="14 6 8 12 14 18" />
          </svg>
        </button>
        <button type="button" className={styles.newBtn} onClick={onNew} aria-label={t('nav.newAria')}>
          {t('nav.new')}
        </button>
      </header>
      <div
        ref={noticeReserveRef}
        className={styles.noticeReserve}
        style={{ height: noticeReserve }}
        data-notice-reserve={noticeReserve}
        aria-hidden="true"
      />

      <div
        ref={scrollRef}
        key={scrollRecoverKey}
        className={styles.scroll}
        data-remember-scroll
        data-recovery-key={scrollRecoverKey}
      >
        {searchActive ? (
          <SearchSurface
            query={searchQuery}
            onQueryChange={changeSearch}
            debouncedQuery={trimmedQuery}
            result={searchResult}
            pending={searchPending}
            page={searchPage}
            onPage={changeSearchPage}
            inputRef={searchInputRef}
            onPrayerTap={openPrayer}
            onShareMoment={onShareMoment}
            mergeMap={mergeMap}
            locale={locale}
            t={t}
          />
        ) : threadKey !== null ? (
          activeThread ? (
            <ThreadArc
              thread={activeThread}
              page={threadPage}
              onPage={setThreadPage}
              onPrayerTap={openPrayer}
              onShareMoment={onShareMoment}
              mergeMap={mergeMap}
              locale={locale}
              t={t}
            />
          ) : (
            <p className={styles.empty} role="status">{t('remember.peopleEmpty')}</p>
          )
        ) : (
          <>
            <header className={styles.hero}>
              <div className={styles.heroScene} aria-hidden="true" />
              <h1 className={styles.heroTitle}>{t('remember.title')}</h1>
              <p className={styles.heroPsalm}>{t('remember.deeds')}</p>
              <p className={styles.heroPsalmCitation}>{t('remember.deedsCitation')}</p>
            </header>

            {!isEmpty && (
              <LensRow
                lens={lens}
                setLens={switchLens}
                onSearch={openSearch}
                searchRef={searchTriggerRef}
                t={t}
              />
            )}

            {isEmpty ? (
              <p className={styles.empty} role="status">
                {prayers.length === 0 ? t('remember.emptyNew') : t('remember.emptyMoments')}
              </p>
            ) : (
              <>
                <section
                  id="remember-panel-people"
                  role="tabpanel"
                  aria-labelledby="remember-tab-people"
                  hidden={lens !== 'people'}
                >
                  {lens === 'people' && (
                    <PeopleLens
                      threads={threads}
                      page={peoplePage}
                      onPage={setPeoplePage}
                      onOpenThread={openThread}
                      t={t}
                    />
                  )}
                </section>
                <section
                  id="remember-panel-time"
                  role="tabpanel"
                  aria-labelledby="remember-tab-time"
                  hidden={lens !== 'time'}
                >
                  {lens === 'time' && (
                    <>
                      {anniversary && (
                        <AnniversaryLine
                          moment={anniversary.moment}
                          yearsAgo={anniversary.yearsAgo}
                          onPrayerTap={openPrayer}
                          t={t}
                        />
                      )}
                      {stickyYear !== null && (
                        <div className={styles.stickyYear} aria-label={t('remember.yearMarkerAria', { year: stickyYear })}>
                          {stickyYear}
                        </div>
                      )}
                      <div
                        ref={wrapRef}
                        className={styles.streamWrap}
                        style={{ height: layout.total }}
                        data-remember-stream-wrap
                      >
                        {visibleIndices.map((index) => {
                          const item = items[index];
                          return (
                            <MeasuredSlot
                              key={item.key}
                              item={item}
                              top={layout.prefix[index]}
                              estimate={estimateItemHeight(item, textScale)}
                              onMeasure={onMeasure}
                            >
                              {item.kind === 'year' ? (
                                <YearMarker year={item.year} t={t} />
                              ) : (
                                <MomentEntry
                                  moment={item.moment}
                                  onPrayerTap={openPrayer}
                                  onShareMoment={onShareMoment}
                                  mergeMap={mergeMap}
                                  locale={locale}
                                  t={t}
                                />
                              )}
                            </MeasuredSlot>
                          );
                        })}
                      </div>
                    </>
                  )}
                </section>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function AnniversaryLine({
  moment,
  yearsAgo,
  onPrayerTap,
  t,
}: {
  moment: Moment;
  yearsAgo: number;
  onPrayerTap: (id: string) => void;
  t: TFn;
}) {
  return (
    <button
      type="button"
      className={styles.anniversary}
      onClick={() => onPrayerTap(moment.prayer.id)}
      aria-label={t('remember.anniversaryAria', { count: yearsAgo })}
    >
      <span className={styles.anniversaryEyebrow}>
        {t('remember.anniversaryEyebrow', { count: yearsAgo })}
      </span>
      <span className={styles.anniversaryTeaser}>{moment.body}</span>
    </button>
  );
}

function LensRow({
  lens,
  setLens,
  onSearch,
  searchRef,
  t,
}: {
  lens: Lens;
  setLens: (lens: Lens) => void;
  onSearch: () => void;
  searchRef: RefObject<HTMLButtonElement | null>;
  t: TFn;
}) {
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const onTabKey = (event: ReactKeyboardEvent<HTMLButtonElement>) => {
    const order: Lens[] = ['time', 'people'];
    const current = order.indexOf(lens);
    let next = current;
    if (event.key === 'ArrowRight') next = (current + 1) % order.length;
    else if (event.key === 'ArrowLeft') next = (current - 1 + order.length) % order.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = order.length - 1;
    else return;
    event.preventDefault();
    setLens(order[next]);
    tabRefs.current[next]?.focus();
  };
  return (
    <div className={styles.lensRow}>
      <div className={styles.lensTabs} role="tablist" aria-label={t('remember.lensRowAria')}>
        {(['time', 'people'] as const).map((candidate, index) => (
          <button
            key={candidate}
            ref={(node) => { tabRefs.current[index] = node; }}
            id={`remember-tab-${candidate}`}
            type="button"
            role="tab"
            aria-selected={lens === candidate}
            aria-controls={`remember-panel-${candidate}`}
            tabIndex={lens === candidate ? 0 : -1}
            className={`${styles.lensBtn} ${lens === candidate ? styles.lensBtnActive : ''}`}
            onClick={() => setLens(candidate)}
            onKeyDown={onTabKey}
          >
            {t(candidate === 'time' ? 'remember.lensTime' : 'remember.lensPeople')}
          </button>
        ))}
      </div>
      <button
        ref={searchRef}
        type="button"
        className={styles.searchTrigger}
        onClick={onSearch}
        aria-label={t('remember.searchOpenAria')}
        data-remember-search-trigger
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="11" cy="11" r="7" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
      </button>
    </div>
  );
}

function SearchSurface({
  query,
  onQueryChange,
  debouncedQuery,
  result,
  pending,
  page,
  onPage,
  inputRef,
  onPrayerTap,
  onShareMoment,
  mergeMap,
  locale,
  t,
}: {
  query: string;
  onQueryChange: (query: string) => void;
  debouncedQuery: string;
  result: RememberSearchResult;
  pending: boolean;
  page: number;
  onPage: (page: number) => void;
  inputRef: RefObject<HTMLInputElement | null>;
  onPrayerTap: (id: string) => void;
  onShareMoment: (prayer: Prayer) => void;
  mergeMap: WhoMergeMap;
  locale: string;
  t: TFn;
}) {
  const inputId = useId();
  const statusId = `${inputId}-status`;
  const queryIsEmpty = query.trim() === '';
  const clearSearch = useCallback(() => {
    onQueryChange('');
    inputRef.current?.focus({ preventScroll: true });
    requestAnimationFrame(() => inputRef.current?.focus({ preventScroll: true }));
  }, [inputRef, onQueryChange]);

  return (
    <div
      className={styles.search}
      data-remember-search-state={pending ? 'pending' : 'current'}
      data-remember-search-query={debouncedQuery}
      data-remember-result-count={pending || queryIsEmpty ? undefined : result.total}
      data-remember-context={pending || queryIsEmpty ? undefined : String(result.context)}
      data-remember-related={pending || queryIsEmpty ? undefined : String(result.related)}
      data-remember-direct-count={pending || queryIsEmpty ? undefined : result.directTotal}
      data-remember-context-count={pending || queryIsEmpty ? undefined : result.contextTotal}
      data-remember-related-count={pending || queryIsEmpty ? undefined : result.relatedTotal}
      data-mounted-search-rows={pending ? 0 : result.moments.length}
    >
      <div className={styles.searchWrap} role="search" aria-label={t('remember.searchLabel')}>
        <label className={styles.srOnly} htmlFor={inputId}>{t('remember.searchLabel')}</label>
        <svg className={styles.searchIcon} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="11" cy="11" r="7" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          id={inputId}
          ref={inputRef}
          type="search"
          className={styles.searchInput}
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== 'Escape' || query === '') return;
            event.preventDefault();
            event.stopPropagation();
            clearSearch();
          }}
          placeholder={t('remember.searchPlaceholder')}
          aria-describedby={!queryIsEmpty ? statusId : undefined}
          aria-busy={pending}
          maxLength={DISCOVERY_SEARCH_MAX_QUERY_LENGTH}
          inputMode="search"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
        />
        {query && (
          <button type="button" className={styles.searchClear} onClick={clearSearch} aria-label={t('remember.searchClear')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
      </div>

      {!queryIsEmpty && (
        <div
          id={statusId}
          className={styles.searchStatus}
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          {pending ? (
            <p className={styles.searchHeading}>{t('remember.searchSearching')}</p>
          ) : result.total > 0 ? (
            <>
              <p className={styles.searchHeading}>
                {t('remember.searchResultsCount', { count: result.total })}
              </p>
              {result.context && (
                <p className={styles.searchRelated}>{t('remember.searchContextNote')}</p>
              )}
              {result.related && (
                <p className={styles.searchRelated}>{t('remember.searchRelatedNote')}</p>
              )}
            </>
          ) : (
            <p className={styles.empty}>{t('remember.searchEmpty')}</p>
          )}
        </div>
      )}

      {!queryIsEmpty && !pending && result.total > 0 && (
        <>
            <div className={styles.arcList}>
              {result.moments.map((moment) => (
                <MomentEntry
                  key={moment.id}
                  moment={moment}
                  flow
                  onPrayerTap={onPrayerTap}
                  onShareMoment={onShareMoment}
                  mergeMap={mergeMap}
                  locale={locale}
                  t={t}
                  searchMatchKind={result.matchKindByMomentId.get(moment.id)}
                />
              ))}
            </div>
            <PageControls page={page} total={result.total} onPage={onPage} t={t} />
        </>
      )}
    </div>
  );
}

function PeopleLens({
  threads,
  page,
  onPage,
  onOpenThread,
  t,
}: {
  threads: MomentThread[];
  page: number;
  onPage: (page: number) => void;
  onOpenThread: (key: string) => void;
  t: TFn;
}) {
  if (threads.length === 0) return <p className={styles.empty}>{t('remember.peopleEmpty')}</p>;
  return (
    <div className={styles.peopleLens}>
      {forwardPage(threads, page).map((thread) => (
        <PersonThreadCard key={thread.key} thread={thread} onOpen={() => onOpenThread(thread.key)} t={t} />
      ))}
      <PageControls page={page} total={threads.length} onPage={onPage} t={t} />
    </div>
  );
}

function PersonThreadCard({
  thread,
  onOpen,
  t,
}: {
  thread: MomentThread;
  onOpen: () => void;
  t: TFn;
}) {
  const displayName = thread.isSelf ? t('entry.praiseLabel') : thread.key;
  const dots = thread.moments.slice(0, 24).slice().reverse();
  return (
    <button
      type="button"
      className={styles.threadCard}
      onClick={onOpen}
      aria-label={t('remember.threadCardAria', { name: displayName, count: thread.count })}
      data-remember-thread={thread.key}
    >
      <span className={styles.threadHeader}>
        <span className={styles.threadName}>{displayName}</span>
        <span className={styles.threadCount}>{t('remember.threadCount', { count: thread.count })}</span>
      </span>
      {thread.moments[0].body && <span className={styles.threadTeaser}>{thread.moments[0].body}</span>}
      <span className={styles.threadSparkline} aria-hidden="true">
        {dots.map((moment) => <span key={moment.id} className={`${styles.spark} ${sparkClass(moment.kind)}`} />)}
      </span>
    </button>
  );
}

function ThreadArc({
  thread,
  page,
  onPage,
  onPrayerTap,
  onShareMoment,
  mergeMap,
  locale,
  t,
}: {
  thread: MomentThread;
  page: number;
  onPage: (page: number) => void;
  onPrayerTap: (id: string) => void;
  onShareMoment: (prayer: Prayer) => void;
  mergeMap: WhoMergeMap;
  locale: string;
  t: TFn;
}) {
  const displayName = thread.isSelf ? t('entry.praiseLabel') : thread.key;
  const chronology = useMemo(() => thread.moments.slice().reverse(), [thread.moments]);
  const span = chronology.length > 1
    ? formatSpanBetween(chronology[0].date, chronology[chronology.length - 1].date, t)
    : '';
  const visible = reverseChronologyPage(chronology, page);
  return (
    <div className={styles.arc}>
      <header className={styles.arcHeader}>
        <h2 className={styles.arcName}>{displayName}</h2>
        <p className={styles.arcMeta}>
          {t('remember.threadCount', { count: thread.count })}
          {span ? ` · ${t('remember.arcSpan', { span })}` : ''}
        </p>
      </header>
      <div className={styles.arcList}>
        {visible.map((moment) => (
          <MomentEntry
            key={moment.id}
            moment={moment}
            flow
            onPrayerTap={onPrayerTap}
            onShareMoment={onShareMoment}
            mergeMap={mergeMap}
            locale={locale}
            t={t}
          />
        ))}
      </div>
      <PageControls page={page} total={chronology.length} onPage={onPage} reverseLabels t={t} />
    </div>
  );
}

function PageControls({
  page,
  total,
  onPage,
  reverseLabels = false,
  t,
}: {
  page: number;
  total: number;
  onPage: (page: number) => void;
  reverseLabels?: boolean;
  t: TFn;
}) {
  const pages = pageCount(total);
  if (pages <= 1) return null;
  return (
    <nav
      className={styles.pagination}
      aria-label={t('remember.paginationAria')}
      data-remember-pagination
    >
      <button type="button" className={styles.pageButton} disabled={page === 0} onClick={() => onPage(page - 1)}>
        {t(reverseLabels ? 'remember.pageNewer' : 'remember.pagePrevious')}
      </button>
      <span className={styles.pageStatus}>{t('remember.pageStatus', { page: page + 1, pages })}</span>
      <button type="button" className={styles.pageButton} disabled={page + 1 >= pages} onClick={() => onPage(page + 1)}>
        {t(reverseLabels ? 'remember.pageOlder' : 'remember.pageNext')}
      </button>
    </nav>
  );
}

function YearMarker({ year, t }: { year: number; t: TFn }) {
  return <div className={styles.yearMarker} aria-label={t('remember.yearMarkerAria', { year })}>{year}</div>;
}

interface MomentEntryProps {
  moment: Moment;
  onPrayerTap: (id: string) => void;
  onShareMoment: (prayer: Prayer) => void;
  mergeMap: WhoMergeMap;
  locale: string;
  t: TFn;
  flow?: boolean;
  searchMatchKind?: RememberSearchMatchKind;
}

function MomentEntry({
  moment,
  onPrayerTap,
  onShareMoment,
  mergeMap,
  locale,
  t,
  flow,
  searchMatchKind,
}: MomentEntryProps) {
  const { prayer } = moment;
  const rawWho = whoOfWithMap(prayer, mergeMap);
  const displayName = isSelf(rawWho)
    ? t('entry.praiseLabel')
    : isGeneral(rawWho)
      ? t('people.general')
      : rawWho;
  const attribution = isPraise(prayer) ? t('entry.praiseLabel') : t('remember.attribution', { name: displayName });
  const formattedDate = formatShortDateTime(moment.date, locale);
  const dateLabel =
    moment.dateSource === 'legacy-updated-at-proxy'
      ? t('remember.dateApproximate', { date: formattedDate })
      : formattedDate;
  let label: string;
  let metaLine: string | null = null;
  let kindClass: string;
  let bodyClass: string;
  let ariaLabelKey: string;

  if (moment.kind === 'progress') {
    label = t('remember.kindProgress');
    kindClass = styles.kindProgress;
    bodyClass = styles.bodyOperational;
    ariaLabelKey = 'remember.entryTapAriaProgress';
  } else if (moment.kind === 'answered') {
    label = t('remember.kindAnswered');
    kindClass = styles.kindAnswered;
    bodyClass = styles.bodyReflective;
    ariaLabelKey = 'remember.entryTapAriaAnswered';
    const span = formatSpanBetween(prayer.createdAt, moment.date, t);
    metaLine = span === t('people.spanToday')
      ? t('remember.answeredMetaSameDay')
      : span ? t('remember.answeredMetaSpan', { span }) : null;
  } else {
    label = t('remember.kindTestimony');
    kindClass = styles.kindTestimony;
    bodyClass = styles.bodyReflective;
    ariaLabelKey = 'remember.entryTapAriaTestimony';
    const span = formatSpanBetween(prayer.createdAt, prayer.answeredAt ?? moment.date, t);
    metaLine = span === t('people.spanToday')
      ? t('remember.answeredMetaSameDay')
      : span ? t('remember.answeredMetaSpan', { span }) : null;
  }

  const searchReason =
    searchMatchKind === 'context'
      ? t('remember.searchMatchedContext')
      : searchMatchKind === 'related'
        ? t('remember.searchMatchedRelated')
        : null;
  const entryAriaLabel = t(ariaLabelKey, { name: displayName, date: dateLabel });

  return (
    <div
      className={`${styles.entry} ${kindClass} ${flow ? styles.entryFlow : ''}`}
      data-moment-id={moment.id}
      data-prayer-id={prayer.id}
      data-body-source={moment.bodySource.kind}
      data-search-match={searchMatchKind}
    >
      <button
        type="button"
        className={styles.entryTap}
        data-moment-open
        onClick={() => onPrayerTap(prayer.id)}
        aria-label={searchReason ? `${entryAriaLabel}. ${searchReason}.` : entryAriaLabel}
      >
        <span className={styles.entryHeader}>
          <span className={styles.entryLabel}>{label}</span>
          <span className={styles.entryDate}>{dateLabel}</span>
        </span>
        {searchReason && <span className={styles.entrySearchReason}>{searchReason}</span>}
        <span className={styles.entryAttribution}>{attribution}</span>
        {momentBody(moment) && <span className={bodyClass}>{momentBody(moment)}</span>}
        {metaLine && <span className={styles.entryMeta}>{metaLine}</span>}
      </button>
      <button
        type="button"
        className={styles.entryShare}
        onClick={() => onShareMoment(prayer)}
        aria-label={t('remember.shareMomentAria', { name: displayName, date: dateLabel })}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <line x1="22" y1="2" x2="11" y2="13" />
          <polygon points="22 2 15 22 11 13 2 9 22 2" />
        </svg>
      </button>
    </div>
  );
}
